import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, LogIn, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import IconButton from '../components/IconButton';

const SpinLoader = (props) => <Loader2 className={`${props.className} animate-spin`} />;

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/dashboard';

  // Redirecionar se já estiver logado
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, from]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        // Tratar erro específico de credenciais
        if (result.message?.includes('Credenciais inválidas')) {
          setErrors({ password: 'Email ou senha incorretos' });
        } else {
          setErrors({ general: result.message || 'Erro ao fazer login' });
        }
      }
    } catch (error) {
      setErrors({ general: 'Erro interno do servidor' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden shadow-2xl">
        {/* Coluna esquerda: Cartão de Login */}
        <div className="bg-white p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Login</h2>
            <p className="text-gray-600">Entre com seu usuário ou email para continuar</p>
          </div>

          {/* Form */}
          <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
            {/* Erro geral */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {errors.general}
              </div>
            )}

            <div className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input pl-10 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="seu@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`input pl-10 pr-10 ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="Sua senha"
                  />
                  <IconButton
                    type="button"
                    icon={showPassword ? EyeOff : Eye}
                    variant="info"
                    size="sm"
                    title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
            </div>

            {/* Forgot password link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="inline-block bg-white text-primary-700 font-semibold px-6 py-2 rounded-none shadow-sm hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                Esqueceu sua senha?
              </Link>
            </div>

            {/* Submit button */}
            <IconButton
              type="submit"
              icon={isSubmitting ? SpinLoader : LogIn}
              variant="primary"
              disabled={isSubmitting}
              title={isSubmitting ? 'Entrando...' : 'Entrar'}
            />
          </form>
        </div>

        {/* Coluna direita: Painel visual com gradiente */}
        <div className="hidden lg:flex bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white items-center justify-center relative">
          <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-white/10 blur-xl" />
          <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-black/10 blur-2xl" />
          <div className="relative z-10 text-center p-10">
            <h3 className="text-3xl font-extrabold tracking-wide">BEM-VINDO!</h3>
            <p className="mt-4 text-sm sm:text-base text-white/80">Entre com seus dados e comece sua jornada conosco</p>
            <Link to="/forgot-password" className="mt-8 inline-block bg-white text-primary-700 font-semibold px-8 py-3 rounded-none shadow-sm hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/50">
              Esqueceu sua senha?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;